import AHBoolean from "common/values/boolean/boolean";
import Date from "common/values/date/date";
import Percent from "common/values/percent/percent";
import Individual from "marketplace/entities/individual/individual";
import { ProposalField } from "work/entities/proposal/proposal";
import { FeeScheduleCategoryRedlineJSON, FeeScheduleRedline } from "work/entities/proposal/redlining/fee-schedule-redline/fee-schedule-redline";
import FieldRedline, {
  FieldRedlineArray,
  FieldRedlineArrayJSON,
  FieldRedlineJSON,
} from "work/entities/proposal/redlining/field-redline";
import { RedlineChange } from "work/entities/proposal/redlining/redline-change";
import ProjectDescription from "work/values/project-description/project-description";
import ProjectName from "work/values/project-name/project-name";
import WorkDocument from "work/values/work-document/work-document";
import { MaskedContracts, MaskedTeam, MaskedDiscount, MaskedFeeSchedule, Masked, MaskedField } from "work/entities/work-agreement/work-agreement";
import moment from "moment";

export interface ProposalRedlineJSON {
  name: FieldRedlineJSON;
  description: FieldRedlineJSON;
  responseDueBy: FieldRedlineJSON;
  startDate: FieldRedlineJSON;
  endDate: FieldRedlineJSON;
  team: FieldRedlineArrayJSON | MaskedField;
  feeSchedule: FeeScheduleCategoryRedlineJSON[] | MaskedField;
  clientPolicyDocuments: FieldRedlineArrayJSON | MaskedField;
  vendorPolicyDocuments: FieldRedlineArrayJSON | MaskedField;
  conflictsCheckWaived: FieldRedlineJSON | MaskedField;
  teamRestricted: FieldRedlineJSON | MaskedField;
  conflictsDocuments: FieldRedlineArrayJSON | MaskedField;
  discount: FieldRedlineJSON | MaskedField;
  lastUpdated: string;
}

export default class ProposalRedline {
  private _name: FieldRedline<ProjectName>;
  private _description: FieldRedline<ProjectDescription>;
  private _responseDueBy: FieldRedline<Date>;
  private _startDate: FieldRedline<Date>;
  private _endDate: FieldRedline<Date>;
  protected _team: FieldRedlineArray<Individual> | MaskedTeam;
  protected _feeSchedule: FeeScheduleRedline | MaskedFeeSchedule;
  protected _clientPolicyDocuments: FieldRedlineArray<WorkDocument> | MaskedContracts;
  protected _vendorPolicyDocuments: FieldRedlineArray<WorkDocument> | MaskedContracts;
  protected _conflictsCheckWaived: FieldRedline<AHBoolean> | MaskedContracts;
  protected _teamRestricted: FieldRedline<AHBoolean> | MaskedTeam;
  protected _conflictsDocuments: FieldRedlineArray<WorkDocument> | MaskedContracts;
  protected _discount: FieldRedline<Percent> | MaskedDiscount;
  protected _lastUpdated: Date;

  constructor(
    name: FieldRedline<ProjectName>,
    description: FieldRedline<ProjectDescription>,
    dates: RedlinedDates,
    team: FieldRedlineArray<Individual> | MaskedTeam,
    feeSchedule: FeeScheduleRedline | MaskedFeeSchedule,
    conflictsCheckWaived: FieldRedline<AHBoolean> | MaskedContracts,
    teamRestricted: FieldRedline<AHBoolean> | MaskedTeam,
    documents: RedlinedDocuments,
    discount: FieldRedline<Percent> | MaskedDiscount,
    lastUpdated: Date
  ) {
    this._name = name;
    this._description = description;
    this._responseDueBy = dates?.responseDueBy;
    this._startDate = dates?.startDate;
    this._endDate = dates?.endDate;
    this._team = team;
    this._feeSchedule = feeSchedule;
    this._clientPolicyDocuments = documents.clientPolicyDocuments;
    this._vendorPolicyDocuments = documents.vendorPolicyDocuments;
    this._conflictsCheckWaived = conflictsCheckWaived;
    this._teamRestricted = teamRestricted;
    this._conflictsDocuments = documents.conflictsDocuments;
    this._discount = discount;
    this._lastUpdated = lastUpdated;
  }

  public get name(): FieldRedline<ProjectName> {
    return this._name;
  }

  public get description(): FieldRedline<ProjectDescription> {
    return this._description;
  }

  public get responseDueBy(): FieldRedline<Date> {
    return this._responseDueBy;
  }

  public get startDate(): FieldRedline<Date> {
    return this._startDate;
  }

  public get endDate(): FieldRedline<Date> {
    return this._endDate;
  }

  public get team(): FieldRedlineArray<Individual> | MaskedTeam {
    return this._team;
  }

  public get feeSchedule(): FeeScheduleRedline | MaskedFeeSchedule {
    return this._feeSchedule;
  }

  public get clientPolicyDocuments(): FieldRedlineArray<WorkDocument> | MaskedContracts {
    return this._clientPolicyDocuments;
  }

  public get vendorPolicyDocuments(): FieldRedlineArray<WorkDocument> | MaskedContracts {
    return this._vendorPolicyDocuments;
  }

  public get conflictsCheckWaived(): FieldRedline<AHBoolean> | MaskedContracts {
    return this._conflictsCheckWaived;
  }

  public get teamRestricted(): FieldRedline<AHBoolean> | MaskedTeam {
    return this._teamRestricted;
  }

  public get conflictsDocuments(): FieldRedlineArray<WorkDocument> | MaskedContracts {
    return this._conflictsDocuments;
  }

  public get discount(): FieldRedline<Percent> | MaskedDiscount {
    return this._discount;
  }

  public get lastUpdated(): Date {
    return this._lastUpdated;
  }

  public set lastUpdated(date: Date) {
    this._lastUpdated = date;
  }

  public get hasRejections(): boolean {
    return (
      this._name.isRejected ||
      this._description.isRejected ||
      this._responseDueBy.isRejected ||
      this._startDate.isRejected ||
      this._endDate.isRejected ||
      (Masked.isNotMasked(this._team) && this._team.hasRejections) ||
      (Masked.isNotMasked(this._feeSchedule) && this._feeSchedule.hasRejections) ||
      (Masked.isNotMasked(this._clientPolicyDocuments) && this._clientPolicyDocuments.hasRejections) ||
      (Masked.isNotMasked(this._vendorPolicyDocuments) && this._vendorPolicyDocuments.hasRejections) ||
      (Masked.isNotMasked(this._conflictsCheckWaived) && this._conflictsCheckWaived.isRejected) ||
      (Masked.isNotMasked(this._teamRestricted) && this._teamRestricted.isRejected) ||
      (Masked.isNotMasked(this._conflictsDocuments) && this._conflictsDocuments.hasRejections) ||
      (Masked.isNotMasked(this._discount) && this._discount.isRejected)
    )
  }

  public get hasRevisions(): boolean {
    return (
      this._name.isRevised ||
      this._description.isRevised ||
      this._responseDueBy.isRevised ||
      this._startDate.isRevised ||
      this._endDate.isRevised ||
      (Masked.isNotMasked(this._team) && this._team.isRevised) ||
      (Masked.isNotMasked(this._feeSchedule) && this._feeSchedule.isRevised) ||
      (Masked.isNotMasked(this._clientPolicyDocuments) && this._clientPolicyDocuments.isRevised) ||
      (Masked.isNotMasked(this._vendorPolicyDocuments) && this._vendorPolicyDocuments.isRevised) ||
      (Masked.isNotMasked(this._conflictsCheckWaived) && this._conflictsCheckWaived.isRevised) ||
      (Masked.isNotMasked(this._teamRestricted) && this._teamRestricted.isRevised) ||
      (Masked.isNotMasked(this._conflictsDocuments) && this._conflictsDocuments.isRevised) ||
      (Masked.isNotMasked(this._discount) && this._discount.isRevised)
    )
  }

  public get hasChanges(): boolean {
    return (
      this._name.hasChanges ||
      this._description.hasChanges ||
      this._responseDueBy.hasChanges ||
      this._startDate.hasChanges ||
      this._endDate.hasChanges ||
      (Masked.isNotMasked(this._team) && this._team.hasChanges) ||
      (Masked.isNotMasked(this._feeSchedule) && this._feeSchedule.hasChanges) ||
      (Masked.isNotMasked(this._clientPolicyDocuments) && this._clientPolicyDocuments.hasChanges) ||
      (Masked.isNotMasked(this._vendorPolicyDocuments) && this._vendorPolicyDocuments.hasChanges) ||
      (Masked.isNotMasked(this._conflictsCheckWaived) && this._conflictsCheckWaived.hasChanges) ||
      (Masked.isNotMasked(this._teamRestricted) && this._teamRestricted.hasChanges) ||
      (Masked.isNotMasked(this._conflictsDocuments) && this._conflictsDocuments.hasChanges) ||
      (Masked.isNotMasked(this._discount) && this._discount.hasChanges)
    )
  }

  public get sessionHistory(): RedlineChange[] {
    return [
      ...this._name.sessionHistory,
      ...this._description.sessionHistory,
      ...this._responseDueBy.sessionHistory,
      ...this._startDate.sessionHistory,
      ...this._endDate.sessionHistory,
      ...Masked.isNotMasked(this._team) ? this._team.sessionHistory : [],
      ...Masked.isNotMasked(this._feeSchedule) ? this._feeSchedule.sessionHistory : [],
      ...Masked.isNotMasked(this._clientPolicyDocuments) ? this._clientPolicyDocuments.sessionHistory : [],
      ...Masked.isNotMasked(this._vendorPolicyDocuments) ? this._vendorPolicyDocuments.sessionHistory : [],
      ...Masked.isNotMasked(this._conflictsCheckWaived) ? this._conflictsCheckWaived.sessionHistory : [],
      ...Masked.isNotMasked(this._teamRestricted) ? this._teamRestricted.sessionHistory : [],
      ...Masked.isNotMasked(this._conflictsDocuments) ? this._conflictsDocuments.sessionHistory : [],
      ...Masked.isNotMasked(this._discount) ? this._discount.sessionHistory : [],
    ];
  }

  public get isResolved(): boolean {
    return (
      this._name.isResolved &&
      this._description.isResolved &&
      this._responseDueBy.isResolved &&
      this._startDate.isResolved &&
      this._endDate.isResolved &&
      (Masked.isNotMasked(this._team) && this._team.isResolved) ||
      (Masked.isNotMasked(this._feeSchedule) && this._feeSchedule.isResolved) ||
      (Masked.isNotMasked(this._clientPolicyDocuments) && this._clientPolicyDocuments.isResolved) ||
      (Masked.isNotMasked(this._vendorPolicyDocuments) && this._vendorPolicyDocuments.isResolved) ||
      (Masked.isNotMasked(this._conflictsCheckWaived) && this._conflictsCheckWaived.isResolved) ||
      (Masked.isNotMasked(this._teamRestricted) && this._teamRestricted.isResolved) ||
      (Masked.isNotMasked(this._conflictsDocuments) && this._conflictsDocuments.isResolved) ||
      (Masked.isNotMasked(this._discount) && this._discount.isResolved)
    );
  }

  public get canBeUndone(): boolean {
    return (
      this._name.canBeUndone ||
      this._description.canBeUndone ||
      this._responseDueBy.canBeUndone ||
      this._startDate.canBeUndone ||
      this._endDate.canBeUndone ||
      (Masked.isNotMasked(this._team) && this._team.canBeUndone) ||
      (Masked.isNotMasked(this._feeSchedule) && this._feeSchedule.canBeUndone) ||
      (Masked.isNotMasked(this._clientPolicyDocuments) && this._clientPolicyDocuments.canBeUndone) ||
      (Masked.isNotMasked(this._vendorPolicyDocuments) && this._vendorPolicyDocuments.canBeUndone) ||
      (Masked.isNotMasked(this._conflictsCheckWaived) && this._conflictsCheckWaived.canBeUndone) ||
      (Masked.isNotMasked(this._teamRestricted) && this._teamRestricted.canBeUndone) ||
      (Masked.isNotMasked(this._conflictsDocuments) && this._conflictsDocuments.canBeUndone) ||
      (Masked.isNotMasked(this._discount) && this._discount.canBeUndone)
    );
  }

  public clearSessionHistory() {
    this._name.clearSessionHistory();
    this._description.clearSessionHistory();
    this._responseDueBy.clearSessionHistory();
    this._startDate.clearSessionHistory();
    this._endDate.clearSessionHistory();
    if (Masked.isNotMasked(this._team))
      this._team.clearSessionHistory();
    if (Masked.isNotMasked(this._feeSchedule))
      this._feeSchedule.clearSessionHistory();
    if (Masked.isNotMasked(this._clientPolicyDocuments))
      this._clientPolicyDocuments.clearSessionHistory();
    if (Masked.isNotMasked(this._vendorPolicyDocuments))
      this._vendorPolicyDocuments.clearSessionHistory();
    if (Masked.isNotMasked(this._conflictsCheckWaived))
      this._conflictsCheckWaived.clearSessionHistory();
    if (Masked.isNotMasked(this._teamRestricted))
      this._teamRestricted.clearSessionHistory();
    if (Masked.isNotMasked(this._conflictsDocuments))
      this._conflictsDocuments.clearSessionHistory();
    if (Masked.isNotMasked(this._discount))
      this._discount.clearSessionHistory();
  }

  public accept() {
    this._name = this._name.accept();
    this._description = this._description.accept();
    this._responseDueBy = this._responseDueBy.accept();
    this._startDate = this._startDate.accept();
    this._endDate = this._endDate.accept();
    if (Masked.isNotMasked(this._team))
      this._team.acceptAll();
    if (Masked.isNotMasked(this._feeSchedule))
      this._feeSchedule.acceptAll();
    if (Masked.isNotMasked(this._clientPolicyDocuments))
      this._clientPolicyDocuments.acceptAll();
    if (Masked.isNotMasked(this._vendorPolicyDocuments))
      this._vendorPolicyDocuments.acceptAll();
    if (Masked.isNotMasked(this._conflictsCheckWaived))
      this._conflictsCheckWaived.accept();
    if (Masked.isNotMasked(this._teamRestricted))
      this._teamRestricted.accept();
    if (Masked.isNotMasked(this._conflictsDocuments))
      this._conflictsDocuments.acceptAll();
    if (Masked.isNotMasked(this._discount))
      this._discount.accept();
  }

  public reject() {
    this._name = this._name.reject();
    this._description = this._description.reject();
    this._responseDueBy = this._responseDueBy.reject();
    this._startDate = this._startDate.reject();
    this._endDate = this._endDate.reject();
    if (Masked.isNotMasked(this._team))
      this._team.rejectAll();
    if (Masked.isNotMasked(this._feeSchedule))
      this._feeSchedule.rejectAll();
    if (Masked.isNotMasked(this._clientPolicyDocuments))
      this._clientPolicyDocuments.rejectAll();
    if (Masked.isNotMasked(this._vendorPolicyDocuments))
      this._vendorPolicyDocuments.rejectAll();
    if (Masked.isNotMasked(this._conflictsCheckWaived))
      this._conflictsCheckWaived.reject();
    if (Masked.isNotMasked(this._teamRestricted))
      this._teamRestricted.reject();
    if (Masked.isNotMasked(this._conflictsDocuments))
      this._conflictsDocuments.rejectAll();
    if (Masked.isNotMasked(this._discount))
      this._discount.reject();
  }

  public undo() {
    this._name = this._name.undo();
    this._description = this._description.undo();
    this._responseDueBy = this._responseDueBy.undo();
    this._startDate = this._startDate.undo();
    this._endDate = this._endDate.undo();
    if (Masked.isNotMasked(this._team))
      this._team.undoAll();
    if (Masked.isNotMasked(this._feeSchedule))
      this._feeSchedule.undoAll();
    if (Masked.isNotMasked(this._clientPolicyDocuments))
      this._clientPolicyDocuments.undoAll();
    if (Masked.isNotMasked(this._vendorPolicyDocuments))
      this._vendorPolicyDocuments.undoAll();
    if (Masked.isNotMasked(this._conflictsCheckWaived))
      this._conflictsCheckWaived.undo();
    if (Masked.isNotMasked(this._teamRestricted))
      this._teamRestricted.undo();
    if (Masked.isNotMasked(this._conflictsDocuments))
      this._conflictsDocuments.undoAll();
    if (Masked.isNotMasked(this._discount))
      this._discount.undo();
  }

  public updateNameRedline(
    newNameRedline: FieldRedline<ProjectName>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._name = newNameRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateDescriptionRedline(
    newDescriptionRedline: FieldRedline<ProjectDescription>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._description = newDescriptionRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateResponseDueByRedline(
    newResponseDueByRedline: FieldRedline<Date>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._responseDueBy = newResponseDueByRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateStartDateRedline(
    newStartDateRedline: FieldRedline<Date>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._startDate = newStartDateRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateEndDateRedline(
    newEndDateRedline: FieldRedline<Date>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._endDate = newEndDateRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateTeamRestrictedRedline(
    newTeamRestrictedRedline: FieldRedline<AHBoolean>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._teamRestricted = newTeamRestrictedRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateTeamMembersRedline(
    newTeamRedline: FieldRedlineArray<Individual>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._team = newTeamRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateFeeScheduleRedline(
    newFeeScheduleRedline: FeeScheduleRedline
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._feeSchedule = newFeeScheduleRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateConflictsCheckWaivedRedline(
    newConflictsCheckWaivedRedline: FieldRedline<AHBoolean>
  ): ProposalRedline {
    const newRedline = this.clone();
    if (Masked.isMasked(this._conflictsCheckWaived) ||
      Masked.isMasked(newRedline._conflictsCheckWaived) ||
      Masked.isMasked(newRedline._conflictsDocuments)
    ) {
      throw new Error("Cannot update conflicts check waived redline when it is masked.");
    }
    const acceptedWaiving = this._conflictsCheckWaived.originalEntry?.value === false &&
      newConflictsCheckWaivedRedline.revisedEntry?.value === true &&
      newConflictsCheckWaivedRedline.isAccepted;
    const rejectedUnwaiving = this._conflictsCheckWaived.originalEntry?.value === true &&
      newConflictsCheckWaivedRedline.revisedEntry?.value === false &&
      newConflictsCheckWaivedRedline.isRejected;
    if (acceptedWaiving || rejectedUnwaiving) {
      newRedline._conflictsDocuments = newRedline._conflictsDocuments.removeAll();
    }
    newRedline._conflictsCheckWaived = newConflictsCheckWaivedRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateConflictsDocumentsRedline(
    newConflictsDocumentsRedline: FieldRedlineArray<WorkDocument>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._conflictsDocuments = newConflictsDocumentsRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateClientPolicyDocumentsRedline(
    newClientPolicyDocumentsRedline: FieldRedlineArray<WorkDocument>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._clientPolicyDocuments = newClientPolicyDocumentsRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateVendorPolicyDocumentsRedline(
    newVendorPolicyDocumentsRedline: FieldRedlineArray<WorkDocument>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._vendorPolicyDocuments = newVendorPolicyDocumentsRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public updateDiscountRedline(
    newDiscountRedline: FieldRedline<Percent>
  ): ProposalRedline {
    const newRedline = this.clone();
    newRedline._discount = newDiscountRedline;
    newRedline._lastUpdated = new Date();
    return newRedline;
  }

  public clone() {
    const newFeeScheduleRedline = Masked.isMasked(this.feeSchedule) ?
      this.feeSchedule :
      this.feeSchedule.clone();
    return new ProposalRedline(
      this._name.clone(this._name.originalRedline),
      this._description.clone(this._description.originalRedline),
      {
        startDate: this._startDate.clone(this._startDate.originalRedline),
        endDate: this._endDate.clone(this._endDate.originalRedline),
        responseDueBy: this._responseDueBy.clone(this._responseDueBy.originalRedline),
      },
      this._team.clone(),
      newFeeScheduleRedline,
      this._conflictsCheckWaived.clone(),
      this._teamRestricted.clone(),
      {
        clientPolicyDocuments: this._clientPolicyDocuments.clone(),
        vendorPolicyDocuments: this._vendorPolicyDocuments.clone(),
        conflictsDocuments: this._conflictsDocuments.clone(),
      },
      this._discount.clone(),
      this._lastUpdated
    );
  }

  public isEqualTo(other?: ProposalRedline): boolean {
    if (!other) return false;

    if (!this._name.isEqualTo(other.name)) return false;
    if (!this._description.isEqualTo(other.description)) return false;
    if (!this._responseDueBy.isEqualTo(other.responseDueBy)) return false;
    if (!this._startDate.isEqualTo(other.startDate)) return false;
    if (!this._endDate.isEqualTo(other.endDate)) return false;

    if (!Masked.isEqual(this._team, other.team)) return false;
    if (!Masked.isEqual(this._feeSchedule, other.feeSchedule)) return false;
    if (!Masked.isEqual(this._clientPolicyDocuments, other.clientPolicyDocuments)) return false;
    if (!Masked.isEqual(this._vendorPolicyDocuments, other.vendorPolicyDocuments)) return false;
    if (!Masked.isEqual(this._conflictsCheckWaived, other.conflictsCheckWaived)) return false;
    if (!Masked.isEqual(this._teamRestricted, other.teamRestricted)) return false;
    if (!Masked.isEqual(this._conflictsDocuments, other.conflictsDocuments)) return false;

    return Masked.isEqual(this._discount, other.discount);
  }

  public toJSON(): ProposalRedlineJSON {
    return {
      name: this._name.toJSON(),
      description: this._description.toJSON(),
      responseDueBy: this._responseDueBy.toJSON(),
      startDate: this._startDate.toJSON(),
      endDate: this._endDate.toJSON(),
      team: this._team.toJSON(),
      feeSchedule: this._feeSchedule.toJSON(),
      clientPolicyDocuments: this._clientPolicyDocuments.toJSON(),
      vendorPolicyDocuments: this._vendorPolicyDocuments.toJSON(),
      conflictsCheckWaived: this._conflictsCheckWaived.toJSON(),
      teamRestricted: this._teamRestricted.toJSON(),
      conflictsDocuments: this._conflictsDocuments.toJSON(),
      discount: this._discount.toJSON(),
      lastUpdated: this._lastUpdated.toJSON()
    };
  }

  private static isMasked(val: unknown): val is MaskedField {
    return typeof val === 'object' && val !== null && 'isMasked' in val;
  }

  public static fromJSON(redliningJson: ProposalRedlineJSON): ProposalRedline {
    const dates = {
      startDate: FieldRedline.fromJSON<Date>(
        new Date(),
        redliningJson.startDate
      ),
      endDate: FieldRedline.fromJSON<Date>(
        new Date(),
        redliningJson.endDate
      ),
      responseDueBy: FieldRedline.fromJSON<Date>(
        new Date(),
        redliningJson.responseDueBy
      )
    };
    const documents = {
      clientPolicyDocuments: ProposalRedline.isMasked(redliningJson.clientPolicyDocuments)
        ? new MaskedContracts()
        : FieldRedlineArray.fromArrayObject<WorkDocument>(
          WorkDocument.Prototype,
          ProposalField.ClientPolicies,
          ProposalField.ClientPolicyDocument,
          redliningJson.clientPolicyDocuments
        ),
      vendorPolicyDocuments: ProposalRedline.isMasked(redliningJson.vendorPolicyDocuments)
        ? new MaskedContracts()
        : FieldRedlineArray.fromArrayObject<WorkDocument>(
          WorkDocument.Prototype,
          ProposalField.VendorPolicies,
          ProposalField.VendorPolicyDocument,
          redliningJson.vendorPolicyDocuments
        ),
      conflictsDocuments: ProposalRedline.isMasked(redliningJson.conflictsDocuments)
        ? new MaskedContracts()
        : FieldRedlineArray.fromArrayObject<WorkDocument>(
          WorkDocument.Prototype,
          ProposalField.Conflicts,
          ProposalField.ConflictsDocument,
          redliningJson.conflictsDocuments
        ),
    };

    return new ProposalRedline(
      FieldRedline.fromJSON<ProjectName>(
        ProjectName.Prototype,
        redliningJson.name
      ),
      FieldRedline.fromJSON<ProjectDescription>(
        ProjectDescription.Prototype,
        redliningJson.description
      ),
      dates,
      ProposalRedline.isMasked(redliningJson.team)
        ? new MaskedTeam()
        : FieldRedlineArray.fromArrayObject<Individual>(
          Individual.Prototype,
          ProposalField.Team,
          ProposalField.TeamMember,
          redliningJson.team
        ),
      !Array.isArray(redliningJson.feeSchedule) && ProposalRedline.isMasked(redliningJson.feeSchedule)
        ? new MaskedFeeSchedule()
        : FeeScheduleRedline.fromArrayObject(redliningJson.feeSchedule),
      ProposalRedline.isMasked(redliningJson.conflictsCheckWaived)
        ? new MaskedContracts()
        : FieldRedline.fromJSON<AHBoolean>(
          AHBoolean.Prototype,
          redliningJson.conflictsCheckWaived
        ),
      ProposalRedline.isMasked(redliningJson.teamRestricted)
        ? new MaskedTeam()
        : FieldRedline.fromJSON<AHBoolean>(
          AHBoolean.Prototype,
          redliningJson.teamRestricted
        ),
      documents,
      ProposalRedline.isMasked(redliningJson.discount)
        ? new MaskedDiscount()
        : FieldRedline.fromJSON<Percent>(
          Percent.Prototype,
          redliningJson.discount
        ),
      Date.fromJSON(redliningJson.lastUpdated) ?? new Date(moment().subtract(
        1000,
        'years'
      ))
    );
  }
}

export type RedlinedDocuments = {
  clientPolicyDocuments: FieldRedlineArray<WorkDocument> | MaskedContracts;
  vendorPolicyDocuments: FieldRedlineArray<WorkDocument> | MaskedContracts;
  conflictsDocuments: FieldRedlineArray<WorkDocument> | MaskedContracts;
};

export type RedlinedDates = {
  startDate: FieldRedline<Date>;
  endDate: FieldRedline<Date>;
  responseDueBy: FieldRedline<Date>;
};
